import React, {ChangeEvent, useMemo} from "react";
import * as yup from "yup";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    Button,
    Col,
    FormFeedback,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row
} from "reactstrap";
import NoSearchResult from "../../../components/Common/NoSearchResult";
import {useFormik} from "formik";
import {IExoFilter, IExoForm, IExoTemplate} from "../../../store/exo/types";
import ExoFilterList from "./ExoFIlters/ExoFilterList";
import {telegramTextLength} from "./TelegramEditor/telegramHtml";
import TelegramEditor from "./TelegramEditor/TelegramEditor";
import {ExoService} from "../../../services/exo.service";
import SelectExoTemplate from "./ExoTemplate/SelectExoTemplate";
import Select from "../../../components/Common/Select";
import {UserService} from "../../../services/user.service";

interface ModalExoProps {
    isOpen: boolean;
    exoId: number | null;
    onClose: () => void;
    onSuccess: () => void;
}

const ModalExo: React.FC<ModalExoProps> = ({isOpen, exoId, onSuccess, onClose}) => {
    const isEditModal = exoId !== null;
    const queryClient = useQueryClient();

    const {
        data: exo,
        isFetching
    } = useQuery({
        queryKey: ["keyExo", exoId],
        queryFn: () => ExoService.getById(exoId || -1),
        enabled: isEditModal,
        refetchOnWindowFocus: false
    });

    const {data: exoTemplates, isFetching: isTemplateFetching} = useQuery({
        queryKey: ["keyExoTemplates"], queryFn: ExoService.getAllTemplates,
        select: (data) => data.data, refetchOnWindowFocus: false
    });

    const {mutate: create, isPending: isPendingCreate} = useMutation({
        mutationFn: (data: IExoForm) => ExoService.create(data),
        onSuccess: onSuccess,
        mutationKey: ["keyCreateExo"],
    });

    const {mutate: update, isPending: isPendingUpdate} = useMutation({
        mutationFn: (data: IExoForm) => ExoService.update(exoId!, data),
        onSuccess: onSuccess,
        mutationKey: ["keyUpdateExo", exoId],
    });

    const exoFilterSchema: yup.ObjectSchema<IExoFilter> = yup.object({
        key: yup.string()
            .trim()
            .required("Key is required"),
        values: yup.array()
            .of(yup.string().trim().required("Value cannot be empty"))
            .min(1, "At least one value is required")
            .required("Values are required"),
    });

    const validationSchema: yup.ObjectSchema<IExoForm> = yup.object({
        name: yup.string()
            .trim()
            .required("Name is required")
            .max(64, "Name must be at most 64 characters"),
        description: yup.string()
            .trim()
            .max(256, "Description must be at most 256 characters")
            .optional(),
        type: yup.mixed<"group" | "user">()
            .oneOf(["group", "user"], "Type must be group or user")
            .required("Type is required"),
        destination: yup.string()
            .trim()
            .required("Destination is required"),
        body_filter: yup.array()
            .of(exoFilterSchema)
            .optional(),
        status: yup.boolean()
            .required(),
        echo_template_id: yup.number()
            .min(0, "Template does not exist")
            .nullable()
            .defined(),
        body_msg: yup.string()
            .trim()
            .required("Message is required")
            .test("tg-length", "Message must be at most 4096 characters",
                v => telegramTextLength(v ?? "") <= 4096),
    });

    const templateBodyMessage = useMemo(() => exoTemplates?.find(et => et.id === exo?.echo_template_id)?.body_msg, [exoTemplates, exo]);
    const initialValues: IExoForm = {
        name: exo?.name || "",
        description: exo?.description || "",
        type: exo?.type || "user",
        destination: exo?.destination || "",
        body_filter: exo?.body_filter || [],
        status: exo?.status || true,
        echo_template_id: exo?.echo_template_id || null,
        body_msg: exo?.body_msg || templateBodyMessage || ""
    }

    const handleSubmitForm = (result: IExoForm) => {
        if (!isEditModal) create(result);
        else update(result);
    };

    const formik = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const selectUsers = (users: any) =>
        users.data.filter((u: any) => !!u.account)
            .map((u: any) => ({value: u.account.tg_name, label: `${u.name} | @${u.account.tg_name}`}))

    const {data: usersOptions, isLoading: isLoadingUsers} = useQuery({
        queryKey: ["keyUsers"], queryFn: UserService.getUsers,
        enabled: formik.values.type === "user", select: selectUsers
    })

    const selectedUser = useMemo(() => usersOptions?.find((o: any) => o.value === formik.values.destination), [usersOptions, formik]);

    const closeModal = () => {
        onClose();
        formik.resetForm();
    };

    const handleTypeDestination = (option: ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue("type", option.target.value);
        formik.setFieldValue("destination", "");
    };

    const handleUserDestination = (option: any) => {
        formik.setFieldValue("destination", option.value || "");
    };

    const handleTelegramTemplate = (value: string, template: IExoTemplate) => {
        formik.setFieldValue("echo_template_id", template.id);
        formik.setFieldValue("body_msg", value);
    };

    const handleTelegramEditor = (value: string) => {
        if (value === initialValues.body_msg) formik.setFieldValue("echo_template_id", initialValues.echo_template_id);
        else formik.setFieldValue("echo_template_id", null);

        formik.setFieldValue("body_msg", value);
    };

    const fieldInvalid = (name: keyof IExoForm) => !!(formik.touched[name] && formik.errors[name]);

    const isPendingSave = isPendingCreate || isPendingUpdate || !!queryClient.isFetching({queryKey: ["keyExoList"]});

    return (
        <Modal isOpen={isOpen} toggle={closeModal}>
            <ModalHeader toggle={closeModal} tag="h4">
                {isEditModal ? "Edit" : "Add"} EXO Notification
            </ModalHeader>
            {isFetching ? <div className="my-4"><NoSearchResult text="Loading..."/></div> : (
                <form onSubmit={formik.handleSubmit}>
                    <ModalBody>
                        <FormGroup>
                            <Label for="name">Name *</Label>
                            <Input
                                id="name"
                                name="name"
                                placeholder="Enter Name..."
                                value={formik.values.name}
                                disabled={isPendingSave}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                invalid={fieldInvalid("name")}
                            />
                            <FormFeedback>{formik.errors.name}</FormFeedback>
                        </FormGroup>

                        <FormGroup>
                            <Label for="description">Description</Label>
                            <Input
                                id="description"
                                name="description"
                                placeholder="Enter Description..."
                                value={formik.values.description}
                                disabled={isPendingSave}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                invalid={fieldInvalid("description")}
                            />
                            <FormFeedback>{formik.errors.description}</FormFeedback>
                        </FormGroup>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="type">Type *</Label>
                                    <Input
                                        id="type"
                                        name="type"
                                        type="select"
                                        placeholder="Select Type..."
                                        value={formik.values.type}
                                        disabled={isPendingSave}
                                        onChange={handleTypeDestination}
                                        onBlur={formik.handleBlur}
                                        invalid={fieldInvalid("type")}
                                    >
                                        <option value="user">User</option>
                                        <option value="group">Group</option>
                                    </Input>
                                    <FormFeedback>{formik.errors.type}</FormFeedback>
                                </FormGroup>
                            </Col>

                            <Col md={6}>
                                <FormGroup>
                                    <Label for="destination">Destination *</Label>
                                    {formik.values.type === "group" ? (
                                        <Input
                                            id="destination"
                                            name="destination"
                                            placeholder="Enter Group Id..."
                                            value={formik.values.destination}
                                            disabled={isPendingSave}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={fieldInvalid("destination")}
                                        />
                                    ) : (
                                        <Select
                                            isSearchable
                                            name="destination"
                                            label="destination"
                                            placeholder="Select User..."
                                            options={usersOptions}
                                            onChange={handleUserDestination}
                                            value={selectedUser}
                                            isLoading={isLoadingUsers}
                                            isDisabled={isLoadingUsers || isPendingSave}
                                        />
                                    )}
                                    <FormFeedback>{formik.errors.destination}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <ExoFilterList
                                value={formik.values.body_filter || []}
                                disabled={isPendingSave}
                                onChange={filters => formik.setFieldValue("body_filter", filters)}
                            />
                        </FormGroup>

                        <hr/>

                        <FormGroup>
                            <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                                <Label className="mb-0">Message</Label>
                                <div className="w-75">
                                    <SelectExoTemplate
                                        isSearchable
                                        disabled={isPendingSave}
                                        selectedId={formik.values.echo_template_id}
                                        templates={exoTemplates || []}
                                        isLoading={isTemplateFetching}
                                        onApply={handleTelegramTemplate}
                                    />
                                </div>
                            </div>

                            <TelegramEditor
                                value={formik.values.body_msg}
                                onChange={handleTelegramEditor}
                                onBlur={() => formik.setFieldTouched("body_msg", true)}
                                disabled={(isTemplateFetching && !!initialValues.echo_template_id) || isPendingSave}
                                invalid={fieldInvalid("body_msg")}
                            />
                            <FormFeedback>{formik.errors.body_msg}</FormFeedback>
                        </FormGroup>
                    </ModalBody>

                    <ModalFooter>
                        <div className="w-100 d-flex justify-content-between align-items-center flex-row-reverse">
                            <Button type="submit" color="success" disabled={!formik.isValid || isPendingSave}>
                                {isPendingSave ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </ModalFooter>
                </form>
            )}
        </Modal>
    )
}

export default ModalExo;